/**
 * Domain-level helpers for the machine widgets (shared by the dashboard
 * mock and the chart transformations). Pure functions / constants only.
 * Color palettes live in the central registry
 * (`@/shared/constants/chartColors`).
 */

/** Vertical Y scroll appears only when the loaded days exceed this
 *  (same threshold as the library's auto-dataZoom rule, section 37). */
export const Y_SCROLL_THRESHOLD = 7;

/** Initial visible rows when the Y scroll is active. */
export const Y_VISIBLE_ROWS = 10;

/**
 * Chart-AREA height tiers for the day-row charts (the card hugs the
 * content): the chart stretches as days accumulate (with the Y scroll
 * windowing the rows, each visible row keeps a readable ~60px band);
 * `max-sm` shrinks the floor on small screens. Concrete heights — never
 * percentages — so the plot cannot collapse under an auto-height card.
 */
export function chartHeightClass(dayCount: number): string {
  if (dayCount > Y_VISIBLE_ROWS) return "h-[760px] max-sm:h-[640px]";
  if (dayCount > 5) return "h-[640px] max-sm:h-[600px]";
  return "h-[480px] max-sm:h-[560px]";
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Parses `dd/MM/yyyy` into the local-midnight epoch ms of that day. */
export function dayStartMs(day: string): number {
  const [d, m, y] = day.split("/").map(Number);
  return new Date(y, m - 1, d).getTime();
}

/** Local midnight `offsetDays` from `dd/MM/yyyy` (calendar-exact, safe
 *  across month and year rollovers — no DST-sensitive ms arithmetic). */
export function dayStartShifted(day: string, offsetDays: number): number {
  const [d, m, y] = day.split("/").map(Number);
  return new Date(y, m - 1, d + offsetDays).getTime();
}

/** Timestamp → `dd/MM/yyyy` axis label (local date). */
export function formatDayLabel(timestamp: number): string {
  const d = new Date(timestamp);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
